import React, { useEffect, useState } from 'react';
import axios from 'axios';
import qs from 'qs';
import { BASE_URL, CAT_URL, CLIENT_ID } from '../../src/environment';
import log from 'loglevel';
import * as Sentry from '@sentry/browser';

import {
   AuthorizationServiceConfiguration,
   RedirectRequestHandler,
   FetchRequestor,
   LocalStorageBackend,
   DefaultCrypto,
   AuthorizationRequest,
} from '@openid/appauth';

import { NoHashQueryStringUtils } from '../utils/noHashQueryStringUtils';
import useLocalStorage from './useLocalStorage';
import { hashFnv32a } from '../utils/hashString';

const AuthenticationContext = React.createContext();

const initialState = {
   given_name: import.meta.env.VITE_GIVEN_NAME,
   family_name: import.meta.env.VITE_FAMILY_NAME,
   email: import.meta.env.VITE_EMAIL,
   code: import.meta.env.VITE_CODE,
   accessToken: import.meta.env.VITE_ACCESSTOKEN,
   refreshToken: import.meta.env.VITE_REFRESHTOKEN,
};

export const useAuthentication = () => {
   const context = React.useContext(AuthenticationContext);

   if (context === undefined) {
      throw new Error(
         '`useAuthentication` must be used with a `AuthenticationProvider`',
      );
   }
   return context;
};

export const AuthenticationProvider = ({ children }) => {
   const [ user, setUser ] = useLocalStorage('user', initialState);
   const [ code, setCode ] = useState(null);
   const [ userHash, setUserHash ] = useState(null);

   useEffect(() => {
      if (user?.email) {
         setUserHash(hashFnv32a(user.email, 32));
      }
   }, [ user.email ]);
   const authorizationHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto(),
   );

   const signed_in = !!user.refreshToken;

   const setRefreshToken = (refreshToken) => setUser({ ...user, refreshToken });
   const setAccessToken = (accessToken) => setUser({ ...user, accessToken });
   const accessToken = user.accessToken;
   const refreshToken = user.refreshToken;

   const authorize = () => {
      AuthorizationServiceConfiguration.fetchFromIssuer(
         `${CAT_URL}`,
         new FetchRequestor(),
      )
         .then((response) => {
            const authRequest = new AuthorizationRequest({
               client_id: CLIENT_ID,
               redirect_uri: `${BASE_URL}/callback`,
               scope: 'openid',
               response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
               state: '123456',
               extras: { pfidpadapterid: 'OAuthAdapterBasicIdentity' },
            });
            authorizationHandler.performAuthorizationRequest(
               response,
               authRequest,
            );
         })
         .catch((error) => {
            log.error(error);
         });
   };

   // Handles the full logout process:
   // - Revoke the refresh token
   // - Clears the user's session data from local storage and Sentry
   // - Redirects the user to the Azure AD B2C logout endpoint to terminate the identity provider session
   const logout = async () => {
      try {
         // Changed: use qs.stringify instead of stringify
         await axios.post(
            `${CAT_URL}oauth2/v2.0/logout`,
            qs.stringify({
               token: refreshToken,
               token_type_hint: 'refresh_token',
               client_id: CLIENT_ID,
            }),
            {
               headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
               },
            },
         );
      } catch (error) {
         log.warn('Token revocation failed or not supported:', error);
      } finally {
         // Clear local session
         setUser(initialState);

         // Remove the user object for Sentry on logout
         Sentry.configureScope((scope) => scope.setUser(null));

         // Redirect to Azure AD B2C logout
         const azureLogoutUrl = `${CAT_URL}oauth2/v2.0/logout`;
         window.location.href = `${azureLogoutUrl}?post_logout_redirect_uri=${encodeURIComponent(
            BASE_URL,
         )}`;
      }
   };

   const context = {
      user,
      userHash,
      signed_in,
      authorize,
      logout,
      setRefreshToken,
      setAccessToken,
      accessToken,
      refreshToken,
      setUser,
      code,
      setCode,
   };

   return (
      <AuthenticationContext.Provider value={context}>
         {children}
      </AuthenticationContext.Provider>
   );
};
